function isMacSafari() {
  const ua = window.navigator.userAgent.toLowerCase();
  const isSafari =
    ua.includes("safari") && !ua.includes("chrome") && !ua.includes("chromium");
  const isMac = window.navigator.platform.toLowerCase().includes("mac");
  return isSafari && isMac;
}

export function createMacSafariDatePickerCloser() {
  // Step 1: 対象環境を macOS Safari のみに限定する
  const isMacOSSafari = isMacSafari();
  let safariFocusSink: HTMLButtonElement | null = null;

  // Step 4: date input から確実にフォーカスを外すための退避先を生成する
  function getSafariFocusSink() {
    if (safariFocusSink) return safariFocusSink;
    const sink = document.createElement("button");
    sink.type = "button";
    sink.setAttribute("aria-hidden", "true");
    sink.tabIndex = -1;
    sink.style.position = "fixed";
    sink.style.left = "-9999px";
    sink.style.top = "0";
    sink.style.width = "1px";
    sink.style.height = "1px";
    sink.style.opacity = "0";
    sink.style.pointerEvents = "none";
    document.body.appendChild(sink);
    safariFocusSink = sink;
    return sink;
  }

  // Step 6-1: スクロール可能な要素かどうかを判定する
  function canScroll(node: Element | null) {
    if (!(node instanceof HTMLElement)) return false;
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    const scrollableOverflow =
      overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
    return node.scrollHeight > node.clientHeight && scrollableOverflow;
  }

  // Step 6-2: date input から最寄りのスクロール可能コンテナを探す
  function findScrollableAncestor(startEl: HTMLElement | null) {
    let current = startEl?.parentElement || null;
    while (current) {
      if (canScroll(current)) return current;
      current = current.parentElement;
    }
    return null;
  }

  // Step 6: blur だけで閉じないケース向けに 1px スクロール往復を実行する
  function nudgeScrollToCloseDatePicker(inputEl: HTMLInputElement) {
    if (!isMacOSSafari) return;
    const scrollContainer = findScrollableAncestor(inputEl);
    const originalTop = scrollContainer
      ? scrollContainer.scrollTop
      : window.scrollY;
    const originalX = window.scrollX;

    const doNudge = () => {
      if (scrollContainer) {
        scrollContainer.scrollTop = originalTop + 1;
        scrollContainer.scrollTop = originalTop;
      } else {
        window.scrollTo(originalX, originalTop + 1);
        window.scrollTo(originalX, originalTop);
      }
    };

    doNudge();
    window.requestAnimationFrame(doNudge);
    window.setTimeout(doNudge, 16);
  }

  // Step 5: blur -> 退避先focus -> 再blur で picker を閉じる
  function forceCloseSafariDatePicker(inputEl: HTMLInputElement) {
    if (!isMacOSSafari) return;
    inputEl.blur();
    const sink = getSafariFocusSink();
    sink.focus({ preventScroll: true });
    window.setTimeout(() => {
      inputEl.blur();
      if (document.activeElement === inputEl) {
        sink.focus({ preventScroll: true });
      }
      nudgeScrollToCloseDatePicker(inputEl);
    }, 0);
  }

  // Step 3: change/input イベントから Safari 向けクローズ処理を呼ぶ
  function onDateSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    forceCloseSafariDatePicker(input);
  }

  // Step 2: Safari 用スタイル切替のため html にクラスを付与する
  function enableSafariDateInputClass() {
    if (!isMacOSSafari) return;
    document.documentElement.classList.add("is-mac-safari");
  }

  // Step 7: 付与したクラスと退避要素を確実に後片付けする
  function disableSafariDateInputClass() {
    if (!isMacOSSafari) return;
    document.documentElement.classList.remove("is-mac-safari");
    if (safariFocusSink) {
      safariFocusSink.remove();
      safariFocusSink = null;
    }
  }

  return {
    isMacOSSafari,
    onDateSelected,
    enableSafariDateInputClass,
    disableSafariDateInputClass,
  };
}
